/**
 * usePaymentMutations — R6/MP (Manual Payment)
 *
 * Create / Update / Delete fuer manuell erfasste Zahlungen.
 * v1-Paritaet zu app/controllers/payments_controller.rb#create/#update/#destroy,
 * BE-Contract: POST /api/v2/payments, PATCH|DELETE /api/v2/payments/{id}.
 *
 * Bewusste Abweichungen von v1 (im BE dokumentiert, hier nur relevant fuers UI):
 *   * contact_id ist PFLICHT beim Anlegen (422 invalid_contact).
 *   * currency ist PFLICHT beim Anlegen (422 invalid_currency).
 *   * Ohne eigene Wallet gibt es 422 no_wallet statt einer Pseudo-Wallet.
 *   * Edit/Delete gelten NUR noch fuer MANUELL erfasste Zahlungen. Importierte
 *     Blockchain-Payments (`manual:false`) sind schreibgeschuetzt und
 *     antworten mit 403 imported_readonly — das FE blendet die Zeilen-Aktionen
 *     dort komplett aus (PaymentRow), der Code bleibt als Sicherheitsnetz
 *     fuer Race-Conditions uebersetzt.
 *   * Haengt eine Zahlung bereits an einer Auszahlung, antwortet das Backend
 *     mit 422 payment_in_payout.
 *
 * Fehlerbehandlung: das Backend antwortet mit englischem Klartext in `error` und
 * einem stabilen `code`. Der Hook reicht NUR den Code nach oben (paymentErrorCode),
 * die Uebersetzung passiert im UI via payments:errors.<code>.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { apiErrorCode } from '../../lib/errors/apiErrorMessage'

// --- Typen ---

/** v1 _form.haml:9 — 'Cash' wird serverseitig kleingeschrieben gespeichert. */
export const PAYMENT_METHODS = ['hnt', 'paypal', 'money_transfer', 'cash'] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

/**
 * v1 Currency.select_options(hnt: true) — Krypto vor der Fiat-Liste.
 * R6-Review F1: 'mobile' ist eine EIGENE Waehrung und war bisher gar nicht
 * waehlbar; die Option 'iot' war faelschlich mit "IOT / MOBILE" beschriftet.
 */
export const CRYPTO_CURRENCIES = ['hnt', 'iot', 'mobile'] as const
/** Fiat-Kuerzel, die das Backend ueber die Currency-Tabelle akzeptiert. */
export const FIAT_CURRENCIES = ['eur', 'usd', 'chf', 'gbp', 'cad', 'aud', 'jpy'] as const

/** Alle Waehrungen, die das Formular als bekannte Option anbietet. */
export const KNOWN_CURRENCIES: readonly string[] = [...CRYPTO_CURRENCIES, ...FIAT_CURRENCIES]

export type PaymentFormData = {
  amount: number
  currency: string
  currency_amount?: number | null
  /** Pflicht beim Anlegen; beim Bearbeiten entfernt `null` die Zuordnung. */
  contact_id: number | null
  payment_method: string
  memo?: string | null
  /** ISO8601 — Zukunftsdaten werden mit 422 invalid_time abgelehnt. */
  time?: string | null
}

export type PaymentErrorCode =
  | 'invalid_contact'
  | 'no_wallet'
  | 'invalid_amount'
  | 'invalid_currency'
  | 'invalid_payment_method'
  | 'invalid_time'
  | 'duplicate_payment'
  | 'invalid_payment'
  /** 403 — importierte Blockchain-Zahlungen sind schreibgeschuetzt. */
  | 'imported_readonly'
  /** 422 — die Zahlung haengt bereits an einer Auszahlung. */
  | 'payment_in_payout'
  | 'not_found'
  | 'unknown'

const KNOWN_CODES: readonly string[] = [
  'invalid_contact',
  'no_wallet',
  'invalid_amount',
  'invalid_currency',
  'invalid_payment_method',
  'invalid_time',
  'duplicate_payment',
  'invalid_payment',
  'imported_readonly',
  'payment_in_payout',
]

/**
 * Normalisiert einen beliebigen Mutations-Fehler auf einen bekannten Code.
 * Der englische Klartext des Backends wird bewusst NICHT durchgereicht.
 */
export function paymentErrorCode(err: unknown): PaymentErrorCode {
  const code = apiErrorCode(err)
  if (code && KNOWN_CODES.includes(code)) return code as PaymentErrorCode

  const status =
    err && typeof err === 'object' && 'status' in err
      ? (err as { status?: number }).status
      : undefined
  if (status === 404) return 'not_found'
  return 'unknown'
}

function invalidatePaymentQueries(queryClient: ReturnType<typeof useQueryClient>) {
  // Liste + Contact-Detail-Zahlungen
  void queryClient.invalidateQueries({ queryKey: ['payments'] })
  void queryClient.invalidateQueries({ queryKey: ['contact-payments'] })
  void queryClient.invalidateQueries({ queryKey: ['contacts'] })
  // Manuelle Zahlungen reduzieren den offenen Auszahlungsbetrag
  // (api/v2/commissions_controller.rb:103-119) — Provisions-/Payout-Ansichten
  // muessen daher ebenfalls neu geladen werden.
  void queryClient.invalidateQueries({ queryKey: ['commissions'] })
  void queryClient.invalidateQueries({ queryKey: ['payouts'] })
}

// --- Create ---

export function useCreatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: PaymentFormData) => {
      const result = await apiClient.POST('/api/v2/payments', {
        body: { payment: data },
      })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => invalidatePaymentQueries(queryClient),
  })
}

// --- Update ---

export function useUpdatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number | string; data: PaymentFormData }) => {
      const result = await apiClient.PATCH('/api/v2/payments/{id}', {
        params: { path: { id: String(id) } },
        body: { payment: data },
      })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => invalidatePaymentQueries(queryClient),
  })
}

// --- Update Contact (P1 — v1-Paritaet zu payments_controller#update_contact) ---

/**
 * Reine Contact-Reassignment — anders als useUpdatePayment auch fuer
 * IMPORTIERTE Zahlungen erlaubt (Backend: PATCH /api/v2/payments/{id} mit
 * NUR contact_id im Body umgeht die imported_readonly-Sperre gezielt fuer
 * dieses eine Feld, siehe app/controllers/api/v2/payments_controller.rb#update).
 * `contact_id: null` entfernt die Zuordnung wieder.
 */
export function useUpdatePaymentContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, contact_id }: { id: number | string; contact_id: number | null }) => {
      const result = await apiClient.PATCH('/api/v2/payments/{id}', {
        params: { path: { id: String(id) } },
        body: { payment: { contact_id } },
      })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => invalidatePaymentQueries(queryClient),
  })
}

// --- Delete ---

export function useDeletePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number | string) => {
      const result = await apiClient.DELETE('/api/v2/payments/{id}', {
        params: { path: { id: String(id) } },
      })
      if (result.response?.ok === false && result.error) throw result.error
      return result.data
    },
    onSuccess: () => invalidatePaymentQueries(queryClient),
  })
}
